/** sessionStorage keys used across the app. */
export const STORAGE_KEYS = {
  LAYOUT: 'qr-menu:layout',
  QR_TOKEN: 'pospoint-qr-token',
  TABLE_UUID: 'pospoint-qr-table',
  BILL_CONFIRMED: 'pospoint-qr-bill-confirmed',
  CART: 'qr-menu:cart',
} as const;

/** QR REST API path segments (appended to environment.apiUrl). */
export const QR_API = {
  BASE: 'api/qr/',
  ORG: 'org',
  MENU: 'menu',
  PROMOS: 'promos',
  BANNER: 'banner',
  PAYMENT_PROVIDERS: 'payment-providers',
  // Table-service: api/qr/table/{tableUuid}/{orders|call-waiter},
  // api/qr/table/{tableUuid}/orders/{orderId}/{items|request-bill|checkout-link}
  TABLE: 'table',
  ORDERS: 'orders',
  CALL_WAITER: 'call-waiter',
  ITEMS: 'items',
  REQUEST_BILL: 'request-bill',
  CHECKOUT_LINK: 'checkout-link',
} as const;

/** Default "group" header sent with the org request. */
export const DEFAULT_ORG_GROUP = 'qrmenu';

export const DEFAULT_PRIMARY_COLOR = '#000000';

/**
 * Visual templates for the public menu. `default` is the built-in live design (no theme file);
 * the rest are runtime-loaded CSS in `public/themes/<key>.css`. The order here is arbitrary —
 * the admin dialog owns display order; this is only the whitelist.
 */
export const TEMPLATE_KEYS = ['default', 'spotlight', 'minimal', 'italian', 'graphite'] as const;
export type TemplateKey = (typeof TEMPLATE_KEYS)[number];
export const DEFAULT_TEMPLATE: TemplateKey = 'default';

/**
 * Whitelist-validates a raw template string (from the backend or a `?template=` preview param).
 * Trims + lowercases, then matches the whitelist. Returns `null` for absent/blank/unknown input
 * so callers can decide the fallback: the backend read coerces to `default`, the preview override
 * falls through to the backend value. The front never trusts the backend's value here.
 */
export function coerceTemplate(raw: string | null | undefined): TemplateKey | null {
  const value = raw?.trim().toLowerCase() ?? '';
  return (TEMPLATE_KEYS as readonly string[]).includes(value) ? (value as TemplateKey) : null;
}

/** Base font size (px) the menu is designed at; the admin's qrTextSize scales from this. */
export const DEFAULT_TEXT_SIZE = 16;

/** Bounds (px) for the admin-set base font size (qrTextSize); values outside are clamped. */
export const TEXT_SIZE_RANGE = { min: 14, max: 20 } as const;

/** Inline SVG placeholder for missing/broken images — deliberately no network dependency. */
export const FALLBACK_IMAGE =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='800' height='800'><rect width='800' height='800' fill='%23f1f5f9'/><g fill='none' stroke='%2394a3b8' stroke-width='14' stroke-linejoin='round'><rect x='250' y='280' width='300' height='240'/><circle cx='330' cy='360' r='28' fill='%2394a3b8' stroke='none'/><path d='M280 500l90-100 70 80 60-70 80 90'/></g></svg>";

